/**
 * SET_TYPES Configuration
 * 
 * This file provides utilities for working with Set Types (formerly Product Types).
 * All set type values must be dynamically fetched from the product_masters table
 * via the API, and never hardcoded.
 * 
 * The term "product" has been renamed to "set", so all product-type related
 * values must be dynamically referenced from this file.
 */

import React from 'react';
import { masterDropdownService, MasterOption } from '../services/masterDropdownService';

/**
 * Set Type Option interface
 */
export interface SetTypeOption {
  value: string;
  label: string;
}

/**
 * Cache for set types to avoid repeated API calls
 */
let setTypesCache: SetTypeOption[] | null = null;
let setTypesCachePromise: Promise<SetTypeOption[]> | null = null;

/**
 * Get all set types from the API (cached)
 * @returns Promise resolving to array of set type options
 */
export async function getSetTypes(): Promise<SetTypeOption[]> {
  // Return cached value if available
  if (setTypesCache) {
    return setTypesCache;
  }

  // Return existing promise if already fetching
  if (setTypesCachePromise) {
    return setTypesCachePromise;
  }

  // Fetch from API
  setTypesCachePromise = masterDropdownService
    .getSetsDropdown()
    .then((options: MasterOption[]) => {
      const setTypes: SetTypeOption[] = options.map(opt => ({
        value: String(opt.value), // Ensure value is always a string
        label: String(opt.label),
      }));
      setTypesCache = setTypes;
      setTypesCachePromise = null;
      return setTypes;
    })
    .catch((error) => {
      console.error('Error fetching set types:', error);
      setTypesCachePromise = null;
      return [];
    });

  return setTypesCachePromise;
}

/**
 * Clear the set types cache (useful after updates)
 */
export function clearSetTypesCache(): void {
  setTypesCache = null;
  setTypesCachePromise = null;
}

/**
 * Get set type values as an array of strings
 * @returns Promise resolving to array of set type values
 */
export async function getSetTypeValues(): Promise<string[]> {
  const setTypes = await getSetTypes();
  return setTypes.map(st => st.value);
}

/**
 * Get set type labels as an array of strings
 * @returns Promise resolving to array of set type labels
 */
export async function getSetTypeLabels(): Promise<string[]> {
  const setTypes = await getSetTypes();
  return setTypes.map(st => st.label);
}

/**
 * Check if a value is a valid set type
 * @param value The value to check
 * @returns Promise resolving to boolean indicating if value is valid
 */
export async function isValidSetType(value: string): Promise<boolean> {
  const setTypes = await getSetTypes();
  return setTypes.some(st => st.value === value || st.label === value);
}

/**
 * Get set type label by value
 * @param value The set type value
 * @returns Promise resolving to the label or undefined if not found
 */
export async function getSetTypeLabel(value: string): Promise<string | undefined> {
  const setTypes = await getSetTypes();
  const setType = setTypes.find(st => st.value === value || st.label === value);
  return setType?.label;
}

/**
 * Get set type value by label
 * @param label The set type label
 * @returns Promise resolving to the value or undefined if not found
 */
export async function getSetTypeValue(label: string): Promise<string | undefined> {
  const setTypes = await getSetTypes();
  const setType = setTypes.find(st => st.label === label || st.value === label);
  return setType?.value;
}

/**
 * React hook to get set types (for use in components)
 * This is a convenience wrapper that can be used in React components
 */
export function useSetTypes() {
  const [setTypes, setSetTypes] = React.useState<SetTypeOption[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    getSetTypes()
      .then((types) => {
        setSetTypes(types);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Failed to load set types');
        setLoading(false);
      });
  }, []);

  const refetch = React.useCallback(() => {
    clearSetTypesCache();
    return getSetTypes();
  }, []);

  return { setTypes, loading, error, refetch };
}

/**
 * Helper functions for set type comparisons
 * These functions check if a value matches a set type by comparing against
 * the dynamically loaded set types from the database.
 */

/**
 * Check if a value matches a set type (case-insensitive)
 * @param value The value to check
 * @param setTypeName The set type name to match against (e.g., "door", "window")
 * @returns Promise resolving to boolean
 */
export async function isSetType(value: string, setTypeName: string): Promise<boolean> {
  const setTypes = await getSetTypes();
  const normalizedValue = value.toLowerCase().trim();
  const normalizedSetType = setTypeName.toLowerCase().trim();
  
  return setTypes.some(st => {
    const stValue = st.value.toLowerCase().trim();
    const stLabel = st.label.toLowerCase().trim();
    return (stValue === normalizedSetType || stLabel === normalizedSetType) &&
           (stValue === normalizedValue || stLabel === normalizedValue);
  });
}

/**
 * Get a set type value by a common name (case-insensitive)
 * Useful for getting the actual database value from a common name
 * @param commonName Common name like "door", "window", "ventilator", "net fix"
 * @returns Promise resolving to the set type value or undefined
 */
export async function getSetTypeByCommonName(commonName: string): Promise<string | undefined> {
  const setTypes = await getSetTypes();
  const normalized = commonName.toLowerCase().trim();
  
  // Common name mappings (these should ideally come from the database)
  const commonNameMap: Record<string, string[]> = {
    'door': ['door'],
    'window': ['window'],
    'ventilator': ['ventilator'],
    'net fix': ['net fix', 'netfix', 'net fixing'],
    'netfix': ['net fix', 'netfix', 'net fixing'],
  };
  
  const possibleNames = commonNameMap[normalized] || [normalized];
  
  for (const st of setTypes) {
    const stValue = st.value.toLowerCase().trim();
    const stLabel = st.label.toLowerCase().trim();
    if (possibleNames.some(name => stValue === name || stLabel === name)) {
      return st.value;
    }
  }
  
  return undefined;
}

